import { useCallback, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppContext } from '../../Context/AppContext';
import { RootState } from '../../Redux/Store';
import { useAppDispatch, useAppSelector } from '../../Hooks/Hooks';
import {
  clearGlobalSearch,
  handleResetSearchQuery,
  handleResetSearchQueryError,
  handleResetSortAndFilter,
  updateSearchQuery,
  updateSearchResultData,
  updateSearchSectionSelectedTab,
  updateShowSearchResult,
} from '../../Redux/Reducers/SearchSlice';
import { fbLogEvent } from '../../Helpers/FirebaseHelper';
import { isEmptyObject, scrollIntoView } from '../../Utils/Basic';
import { createNatNalSearchPayload } from '../../Utils/App.utils';
import { EVENT, SCREEN, TAB } from '../../Constants/AnalyticsConstants';
import { TSearchTabs } from './SearchContainer.type';
import ToggleSearchTabs from '../../Components/ToggleSearchTabs/ToggleSearchTabs';
import NewSearchContainer from '../../Components/Search/NewSearchContainer/NewSearchContainer';
import RecentSearchContainer from '../../Components/Search/RecentSearchContainer/RecentSearchContainer';
import FavoriteSearchContainer from '../../Components/Search/FavoriteSearchContainer/FavoriteSearchContainer';
import ResultContainer from '../../Components/ResultContainer/ResultContainer';
import EOL from '../../Components/Shared/EOL/EOL';
import './SearchContainer.scss';

export const searchTabs: TSearchTabs[] = [
  {
    slug: 'NewSearch',
    WebTitle: 'New Search',
    MobTitle: 'New',
    selected: true,
  },
  {
    slug: 'RecentSearch',
    WebTitle: 'Recent Search',
    MobTitle: 'Recent',
    selected: false,
  },
  {
    slug: 'FavoriteSearch',
    WebTitle: 'Favorite Search',
    MobTitle: 'Favorite',
    selected: false,
  },
  {
    slug: 'WatchedLoads',
    WebTitle: 'Watched Loads',
    selected: false,
  },
];

const SearchContainer = () => {
  const dispatch = useAppDispatch();
  const { isMobile } = useContext(AppContext);
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const { watchedLoadsData } = useAppSelector((state: RootState) => state.search);
  const { searchSectionSelectedTab, loadByIdData } = useAppSelector(
    (state: RootState) => state?.search,
  );
  const { configTableData } = useAppSelector((state: RootState) => state.ContentOrConfig);
  const appConfigData = configTableData?.FPO_APP_CONFIG;
  const showBanner = useAppSelector((state: RootState) => state.ContentOrConfig?.showBanner);
  const { nextAvailableLocation, nextAvailableTime } = useAppSelector(
    (state: RootState) => state.NatNal,
  );
  useEffect(() => {
    return () => {
      if (!state?.isReload) {
        dispatch(
          updateSearchResultData({
            totalRecords: 0,
            loadPostings: [],
          }),
        );
      }
    };
  }, []);

  useEffect(() => {
    if (!isEmptyObject(state) && state?.isReload) {
      navigate(pathname, { replace: true, state: null });
      handleTabChange('NewSearch');
      scrollIntoView('scrollSearch_Container_Header_Web');
      dispatch(updateShowSearchResult(false));
      dispatch(
        updateSearchResultData({
          totalRecords: 0,
          loadPostings: [],
        }),
      );
      loadByIdData && dispatch(clearGlobalSearch());
    }
  }, [state]);

  const handleTabChange = (slug: string) => {
    if (slug === 'NewSearch' && nextAvailableLocation && nextAvailableTime && !state?.isReload) {
      const payload = createNatNalSearchPayload(
        nextAvailableLocation,
        nextAvailableTime,
        appConfigData,
      );
      dispatch(updateSearchQuery(payload));
      dispatch(clearGlobalSearch());
    } else if (slug === 'NewSearch' && !isEmptyObject(state) && state?.isReload) {
      dispatch(
        updateSearchResultData({
          totalRecords: 0,
          loadPostings: [],
        }),
      );
      loadByIdData && dispatch(clearGlobalSearch());
    } else {
      dispatch(handleResetSearchQuery());
      handleResetSearchCriterial();
    }
    dispatch(handleResetSearchQueryError());
    dispatch(
      updateSearchSectionSelectedTab(
        searchSectionSelectedTab?.map((item: TSearchTabs) => {
          return { ...item, selected: item?.slug === slug };
        }),
      ),
    );
    if (state?.isReload) window?.history?.replaceState(null, '', pathname);
    else if (slug !== 'NewSearch') {
      dispatch(handleResetSearchQuery());
    }
    switch (slug) {
      case 'NewSearch':
        return fbLogEvent(EVENT.UE_VIEW_SEARCH_NEW, {
          screen: SCREEN.SEARCH,
          tab: TAB.NEW,
        });
      case 'RecentSearch':
        return fbLogEvent(EVENT.UE_VIEW_SEARCH_RECENT, {
          screen: SCREEN.SEARCH,
          tab: TAB.RECENT,
        });
      case 'FavoriteSearch':
        return fbLogEvent(EVENT.UE_VIEW_SEARCH_FAVORITE, {
          screen: SCREEN.SEARCH,
          tab: TAB.FAVORITE,
        });
      case 'WatchedLoads':
        return fbLogEvent(EVENT.UE_VIEW_WATCHED_LOADS, {
          screen: SCREEN.SEARCH,
          tab: TAB.WATCHED,
        });
      default:
        break;
    }
  };

  const selectedTab = useCallback(() => {
    return searchSectionSelectedTab?.find((item: any) => item?.selected)?.slug;
  }, [searchSectionSelectedTab]);

  const handleResetSearchCriterial = () => {
    !isMobile && dispatch(handleResetSearchQuery());
    dispatch(handleResetSortAndFilter());
    dispatch(handleResetSearchQueryError());
    dispatch(updateShowSearchResult(false));
  };
  const renderSearchComponents = () => {
    switch (selectedTab()) {
      case 'NewSearch':
        return (
          <div className="Result_Wrapper">
            <NewSearchContainer />
          </div>
        );
      case 'RecentSearch':
        return (
          <>
            <RecentSearchContainer />
            <EOL />
          </>
        );
      case 'FavoriteSearch':
        return (
          <>
            <FavoriteSearchContainer />
            <EOL />
          </>
        );
      case 'WatchedLoads':
        return (
          <div className="Result_Wrapper">
            <ResultContainer
              totalRecords={watchedLoadsData?.totalRecords}
              loadPostings={watchedLoadsData?.loadPostings}
              label="Watched Loads"
              isWatchedLoads={true}
              isFavorite={true}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`Search_Container${showBanner ? ' AlertBannerVisible' : ''} ${selectedTab() || ''}`}
      data-testid="SearchContainer"
    >
      {!isMobile && (
        <div
          className="Search_Container_Header_Web"
          id="Search_Container_Header_Web"
          data-testid="Search_Container_Header_Web"
        >
          <p>Search</p>
        </div>
      )}
      <ToggleSearchTabs data={searchSectionSelectedTab} handleTabChange={handleTabChange} />
      <div className={`Search_Section ${selectedTab()}`}>{renderSearchComponents()}</div>
    </div>
  );
};

export default SearchContainer;
